// Pure ticket time helpers, extracted from Tickets.jsx so expiry math is unit-testable
// without needing to render the component or mock Firebase/auth.

export function normalizeTimestampMs(value) {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'object') {
    if ('seconds' in value) return value.seconds * 1000;
    if ('_seconds' in value) return value._seconds * 1000;
  }
  return null;
}

// Primary: activatedAt (app start or kiosk print+activate). Fallback: printedAt
// (kiosk print without explicit activation).
export function getTicketStartMs(ticket) {
  const activatedMs = normalizeTimestampMs(ticket?.activatedAt);
  const printedMs = normalizeTimestampMs(ticket?.printedAt);
  return activatedMs || printedMs || null;
}

// totalDuration is stored in seconds; fall back to duration (minutes) × 60.
export function getTicketTotalDurationSeconds(ticket) {
  const td = Number(ticket?.totalDuration);
  if (Number.isFinite(td) && td > 0) return td;
  const dur = Number(ticket?.duration);
  if (Number.isFinite(dur) && dur > 0) return Math.round(dur * 60);
  return null;
}

// Remaining seconds, clamped to >= 0. Returns 0 when the ticket never started
// or has no resolvable duration (caller should check hasEverStarted separately
// to distinguish "not started" from "expired").
export function computeTicketRemainingSeconds(ticket, now = Date.now()) {
  const startMs = getTicketStartMs(ticket);
  const totalSec = getTicketTotalDurationSeconds(ticket);
  if (!startMs || !totalSec) return 0;

  const elapsed = Math.floor((now - startMs) / 1000);
  const remaining = totalSec - elapsed;
  return remaining > 0 ? remaining : 0;
}

// A ticket is "expired" only if it was ever started and its remaining time has hit 0.
// A ticket that was never activated is not expired, just inactive.
export function isTicketExpired(ticket, now = Date.now()) {
  const hasEverStarted = Boolean(ticket?.activatedAt) || Boolean(ticket?.printedAt);
  if (!hasEverStarted) return false;
  return computeTicketRemainingSeconds(ticket, now) <= 0;
}

// Static "HH:MM" end-time label (start + duration), or null if unresolvable.
export function computeTicketEndTimeLabel(ticket, now = Date.now()) {
  const startMs = getTicketStartMs(ticket);
  const totalSec = getTicketTotalDurationSeconds(ticket);
  if (!startMs || !totalSec) return null;

  const end = new Date(startMs + totalSec * 1000);
  const hh = String(end.getHours()).padStart(2, '0');
  const mm = String(end.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Play time never runs past closing (same rule as the Pi label, rasp-simulator/closing.js).
// Started after closing (after-hours event): no cap.
export function capPlaySecondsAtClosing(totalSec, secondsUntilClosing) {
  return secondsUntilClosing > 0 ? Math.min(totalSec, secondsUntilClosing) : totalSec;
}
